import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { RUTA_INICIAL, type Rol } from '@shared/permisos';
import { PinPad } from '@renderer/components/PinPad';
import type { UsuarioVista } from '@shared/types';
import { ArrowLeft, User as UserIcon, Shield, KeyRound } from 'lucide-react';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

export function LoginPage() {
  const [usuarios, setUsuarios] = useState<UsuarioVista[]>([]);
  const [seleccionado, setSeleccionado] = useState<UsuarioVista | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  /** Sprint 9.4 — true cuando el backend respondió con reto 2FA pendiente. */
  const [esperando2FA, setEsperando2FA] = useState(false);
  const [codigo2FA, setCodigo2FA] = useState('');
  const [verificando2FA, setVerificando2FA] = useState(false);
  const setSesion = useSessionStore((s) => s.setSesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const res = await window.api.auth.listarUsuarios();
      if (res.ok) setUsuarios(res.data);
    })();
  }, []);

  /**
   * Sprint 64 — **Todos los oficios piden PIN.**
   *
   * Antes, tocar el nombre de un mesero entraba sin credencial: el modelo era "la
   * computadora está en el negocio". En la web eso es un bypass de autenticación, así que
   * estaba bloqueado con 403 — y el resultado era que **sólo la dueña podía entrar**, con
   * los otros tres oficios de adorno.
   */
  const handleSeleccionUsuario = (u: UsuarioVista) => {
    setError(null);
    setSeleccionado(u);
  };

  const handlePinComplete = async (pin: string) => {
    if (!seleccionado) return;
    // Sprint 64 — El mismo camino para los cuatro oficios; el 2FA sólo aplica a quien lo
    // tenga enrolado (hoy, el dueño).
    const res = await window.api.auth.loginDueno(seleccionado.id, pin);
    if (res.ok) {
      // Sprint 9.4 — discriminated union: o entra directo, o queda reto 2FA.
      if (res.data.requiere2FA) {
        setEsperando2FA(true);
        setCodigo2FA('');
        setError(null);
      } else {
        setSesion(res.data.sesion);
        // Sprint 64 — A su trabajo: cocina a su cola, los demás a su tablero.
        navigate(RUTA_INICIAL[res.data.sesion.rol as Rol] ?? '/dashboard');
      }
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
      setResetSignal((v) => v + 1);
    }
  };

  const handleVerificar2FA = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!codigo2FA.trim()) {
      setError('Ingresa el código del 2FA');
      return;
    }
    setVerificando2FA(true);
    setError(null);
    const res = await window.api.auth.verificar2FA(codigo2FA.trim());
    setVerificando2FA(false);
    if (res.ok) {
      setSesion(res.data);
      navigate('/dashboard');
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
      setCodigo2FA('');
    }
  };

  const handleCancelar2FA = () => {
    setEsperando2FA(false);
    setCodigo2FA('');
    setSeleccionado(null);
    setError(null);
  };

  return (
    <div className="flex h-full flex-col items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-6">
      <div className="mb-6 text-center">
        <div className="mb-2 text-3xl font-bold text-primary">{negocio?.nombre ?? 'RestaurantPOS'}</div>
        <div className="text-slate-500">Selecciona tu usuario para iniciar</div>
      </div>

      {!seleccionado ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 max-w-3xl">
          {usuarios.map((u) => (
            <button
              key={u.id}
              onClick={() => handleSeleccionUsuario(u)}
              className="card flex flex-col items-center gap-3 hover:bg-slate-50 transition-colors min-w-[160px]"
            >
              {/*
                Foto de usuario: actualmente sin pipeline de storage. Si en
                el futuro se agrega upload de foto, debe servirse vía
                protocolo `branding://` (o un app-asset:// generalizado),
                NO `file://` que Chromium bloquea por webSecurity.
              */}
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary">
                <UserIcon className="h-10 w-10" />
              </div>
              <div className="text-center">
                <div className="font-semibold">{u.nombre}</div>
                <div className="flex items-center justify-center gap-1 text-xs text-slate-500">
                  {u.rol === 'dueño' && <Shield className="h-3 w-3" />}
                  <span className="uppercase tracking-wide">{u.rol}</span>
                </div>
              </div>
            </button>
          ))}

          {usuarios.length === 0 && (
            <div className="col-span-full text-center text-slate-500">
              No hay usuarios. Reinicia el wizard de configuración.
            </div>
          )}
        </div>
      ) : esperando2FA ? (
        <div className="card flex w-full max-w-sm flex-col items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <KeyRound className="h-7 w-7" />
          </div>
          <div className="text-center">
            <div className="font-semibold">Número de tu celular</div>
            <div className="text-sm text-slate-600">
              Abre <strong>Google Authenticator</strong> en tu celular y escribe el número de 6 cifras.
              <br />
              Si no tienes el celular, escribe una de tus claves de emergencia.
            </div>
          </div>
          <form onSubmit={handleVerificar2FA} className="flex w-full flex-col gap-3">
            <input
              type="text"
              inputMode="text"
              autoFocus
              autoComplete="one-time-code"
              value={codigo2FA}
              onChange={(e) => setCodigo2FA(e.target.value)}
              placeholder="123456 ó ABCD2345"
              maxLength={12}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 text-center font-mono text-2xl tracking-widest focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={verificando2FA}
            />
            <button
              type="submit"
              disabled={verificando2FA || codigo2FA.trim().length < 6}
              className="btn-primary w-full"
            >
              {verificando2FA ? 'Verificando…' : 'Verificar'}
            </button>
          </form>
          {error && (
            <div className="w-full rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {error}
            </div>
          )}
          <button onClick={handleCancelar2FA} className="btn-ghost">
            <ArrowLeft className="h-4 w-4" />
            Cancelar y volver al inicio
          </button>
        </div>
      ) : (
        <div className="card flex flex-col items-center gap-4">
          <div className="text-center">
            <div className="font-semibold">{seleccionado.nombre}</div>
            <div className="text-sm text-slate-500">Ingresa tu PIN</div>
          </div>
          <PinPad onComplete={handlePinComplete} error={error ?? undefined} resetSignal={resetSignal} />
          <button
            onClick={() => {
              setSeleccionado(null);
              setError(null);
            }}
            className="btn-ghost"
          >
            <ArrowLeft className="h-4 w-4" />
            Cambiar usuario
          </button>
        </div>
      )}

      {!seleccionado && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
